import { Directive, Input, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import {
  AuctionItem,
  LottoItem,
  LoyaltyStatus,
  LoyaltyStatusService,
  LOYALTY_STATUS,
  MarketplaceItemExtended,
  LottoService,
  MarketplaceService,
  AuctionService,
  LottoQuery,
  MarketplaceQuery,
  AuctionQuery,
} from '@fgb/core';
import { firstValueFrom } from 'rxjs';

export type ItemType = MarketplaceItemExtended | AuctionItem | LottoItem;

export interface IfItemEnabledContext {
  data: LoyaltyStatus;
  // Disabled status type of the item
  type: LOYALTY_STATUS;
  refresh(): void;
}

/**
 * Directive to show or hide an element based on the status of an item.
 * Optionally, you can pass or override an array of disabled statuses to disable the element if the item has one of the statuses.
 * Similar to ngIf else, you can also pass a template to show when the item is disabled.
 * 
 * @example 
 *  <ng-container *ifItemEnabled="item; else disabledTemplate">
 *   <div>I am enabled</div>
 * </ng-container>
 * 
 * <ng-template #disabledTemplate let-status>
 *  <div>I am disabled! {{ status.data | json }}</div>
 * </ng-template>
 * 
 * using the directive exportAs, you can access the status of the item and refresh method by adding #itemEnabled="itemEnabled" to the ng-template,
 * you can also force refresh the item from the api by passing true to the refresh method ex. itemEnabled.refresh(true)
 * @example
 *     <ng-template
            ifItemEnabled
            [ifItemEnabled]="lottoItem"
            [ifItemEnabledElse]="disabledTemplate"
            #itemEnabled="itemEnabled"
          >
            <button (click)="itemEnabled.refresh()">test</button>
      </ng-template>
 */
@Directive({
  selector: '[ifItemEnabled]',
  standalone: true,
  exportAs: 'itemEnabled',
})
export class IfItemEnabled implements OnInit {
  status: LoyaltyStatus;
  isDisabled: boolean = false;

  /**
   * Else template to show when the item is disabled
   */
  private elseTemplate: TemplateRef<any>;
  private item: ItemType;

  /**
   * Default disabled statuses
   * you can override this by passing an array of statuses to ifItemEnabledDisabledStatus
   * ex. *ifItemEnabled="item; disabledStatus: [RewardStatusType.SOLD_OUT, RewardStatusType.COMING_SOON]"
   * if the item has one of the statuses, the element will be marked as disabled
   */
  private disabledStatus: LOYALTY_STATUS[] = [
    LOYALTY_STATUS.SOLD_OUT,
    LOYALTY_STATUS.COMING_SOON,
    LOYALTY_STATUS.LIMIT_REACHED,
    LOYALTY_STATUS.NOT_ENOUGH_POINTS,
    LOYALTY_STATUS.LOCKED,
  ];

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private lottoService: LottoService,
    private lottoQuery: LottoQuery,
    private marketplaceService: MarketplaceService,
    private marketplaceQuery: MarketplaceQuery,
    private auctionService: AuctionService,
    private auctionQuery: AuctionQuery,
    private statusService: LoyaltyStatusService
  ) {}

  @Input() set ifItemEnabled(item: ItemType) {
    if (this.item !== item) {
      this.item = item;
      this.createView();
    }
  }

  /**
   * Override the default disabled statuses, pass an array of statuses to disable the element if the item has one of the statuses.
   */
  @Input() set ifItemEnabledDisabledStatus(statuses: LOYALTY_STATUS[]) {
    this.disabledStatus = statuses;
  }

  /**
   * Similar to ngIf else, pass a template to show when the item is disabled
   * ex. <ng-container *ifItemEnabled="auction(); else disabledTemplate">
   *     ...
   *     </ng-container>
   *
   * <ng-template #disabledTemplate let-status>
   *    <div>I am disabled! {{ status.data | json }}</div>
   * </ng-template>
   */
  @Input() set ifItemEnabledElse(templateRef: TemplateRef<any>) {
    this.elseTemplate = templateRef;
  }

  /**
   * Create the view once all input variables are initialized
   */
  ngOnInit(): void {
    this.createView();
  }

  private createView(): void {
    this.viewContainer.clear();
    this.checkStatus();

    // Check if the item has one of the disabled statuses
    this.isDisabled = this.disabledStatus.some((status) => this.status.status === status);

    // If the item is enabled, create the view
    if (!this.isDisabled) {
      this.viewContainer.createEmbeddedView(this.templateRef);
    } else {
      // If else template is provided, create the else view
      if (this.elseTemplate) {
        /**
         * Pass data and refresh function to the else template, add let-status to ng-template
         * ex. <ng-template #disabledTemplate let-status>
         *        <div>I am disabled! {{ status.data | json }}</div>
         *        <button (click)="status.refresh()">Refresh Me Status</button>
         *     </ng-template>
         */
        const contextContent: IfItemEnabledContext = {
          data: this.status,
          type: this.status.status,
          refresh: this.refresh.bind(this),
        };
        const context = { $implicit: contextContent };
        this.viewContainer.createEmbeddedView(this.elseTemplate, context);
      }
    }
  }

  /**
   * Initialize the status of the item
   */
  checkStatus(): void {
    this.status = this.statusService.getStatus(this.item);
  }

  /**
   * Refresh the view, useful when the status of the item changes ex. coming soon status
   * you can call this function to refresh the view in inside the else template
   * @example <ng-template #disabledTemplate let-status>
   *            <button (click)="status.refresh()">Refresh Me</button>
   *          </ng-template>
   * @see {@link RewardLottoStatus} {@link RewardMarketplaceStatus} {@link RewardAuctionStatus}
   * for more information on how the status is determined and how the coming soon status is handled
   *
   * @param force - forces to refresh the item from the api then assign the new item to the directive and refresh the view
   * force is not tested yet for different item types
   * set force to true if you want to refresh the item from the api
   *
   * @example <ng-template #disabledTemplate let-status>
   *            <button (click)="status.refresh(true)">Refresh Me From API</button>
   *          </ng-template>
   */
  refresh(force: boolean = false): void {
    if (!force) {
      this.createView();
      return;
    }

    let promise: Promise<any> | undefined;
    let getItem: (id: number) => any;
    let id: number;

    /**
     * Check the type of the item and fetch the item from the api
     */
    if (this.item instanceof AuctionItem) {
      id = this.item.Id;
      promise = firstValueFrom(this.auctionService.fetchAuction(id));
      getItem = this.auctionQuery.getEntity.bind(this.auctionQuery);
    } else if (this.item instanceof MarketplaceItemExtended) {
      id = this.item.Id;
      promise = firstValueFrom(this.marketplaceService.fetchMarketplaceItemById(id));
      getItem = this.marketplaceQuery.getEntity.bind(this.marketplaceQuery);
    } else if (this.item instanceof LottoItem) {
      id = this.item.LottoId;
      promise = firstValueFrom(this.lottoService.fetchLottoById(id));
      getItem = this.lottoQuery.getEntity.bind(this.lottoQuery);
    }

    if (promise) {
      promise.then(() => {
        // Get the updated item from api, not tested yet but should fetch item from api
        this.item = getItem(id);
        // Then recreate the view to update status
        this.createView();
      });
    }
  }
}
